import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
	Typography,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	TextField,
	MenuItem,
	Box,
	useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { Languages } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import { EmailTypeDetails } from './utils';
import { getLanguageName } from 'common/locales/utils';
import { CustomerFacingEmail } from 'common/modules/emails/types';
import { isValidEmail } from 'common/utils/validation';
import StripePattern from 'common/components/StripePattern';

export interface Props {
	emailTo: string | null;
	details: EmailTypeDetails | null;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (args: {
		emailType: CustomerFacingEmail;
		emailTo: string;
		language: Languages;
	}) => void;
}

const SendEmailDialog = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { emailTo: _emailTo, details, isOpen, onConfirm, onClose } = props;
	const { heading, description, emailType } = details ?? {};

	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const activeLanguages = useSelector(ShopSelectors.activeShopLanguages);
	const [selectedLang, setSelectedLang] = useState(defaultLanguage);
	const [isLoading, setIsLoading] = useState(false);

	const { register, formState, watch } = useForm<{ email: string }>({
		defaultValues: { email: _emailTo ?? '' },
		mode: 'onChange',
	});
	const emailTo = watch('email');
	const emailRules = {
		required: t('common:validation.cannotBeEmpty', {
			defaultValue: '{{field}} cannot be empty',
			field: t('common:form.emailAddress', 'Email address'),
		}),
		validate: (value: string) =>
			!isValidEmail(value) ? t('common:validation.isNotValidEmail') : true,
	};

	useEffect(() => {
		if (!isOpen) {
			setIsLoading(false);
		}
	}, [isOpen]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedLang(e.target.value as Languages);
	};

	const handleConfirm = () => {
		if (!emailType || !emailTo) {
			return;
		}
		setIsLoading(true);
		onConfirm({
			emailType,
			emailTo,
			language: selectedLang,
		});
	};

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullScreen={isMobile}
			sx={{
				'& .MuiPaper-root': {
					width: '100%',
				},
			}}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Typography variant="h6" sx={{ position: 'relative', zIndex: 2 }}>
					{t('emails.test.dialog.heading', 'Test email')}
				</Typography>
				<StripePattern color="dark" opacity={0.4} />
			</DialogTitle>
			<DialogContent className={classes.content}>
				<Box my={3}>
					<Typography variant="h6">{heading}</Typography>
					<Typography variant="body2">{description}.</Typography>
				</Box>
				<Box>
					<Typography variant="body1" className={classes.emailTo}>
						{t('emails.test.dialog.sendTestEmail', 'Send test email')}
					</Typography>
				</Box>
				<Box mt={2}>
					<TextField
						type="email"
						fullWidth
						variant="outlined"
						label={t('emails.test.dialog.receiver', 'Receiver')}
						disabled={isLoading}
						error={!!formState.errors.email}
						helperText={formState.errors.email?.message}
						{...register('email', emailRules)}
					/>
				</Box>
				<TextField
					sx={{
						mt: 3,
						mb: 1,
					}}
					label={t('emails.test.dialog.languageVersion', 'Language version')}
					select
					variant="outlined"
					fullWidth
					value={selectedLang}
					onChange={handleChange}
					disabled={isLoading}
				>
					{activeLanguages.map((lang, idx) => (
						<MenuItem key={idx} value={lang}>
							{getLanguageName(lang, t)}
						</MenuItem>
					))}
				</TextField>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={onClose} disabled={isLoading}>
					{t('common:actions.cancel')}
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={isLoading || !formState.isValid}
					color="primary"
					variant="contained"
				>
					{isLoading ? (
						<CircularProgress color="inherit" size={20} />
					) : (
						t('emails.test.dialog.sendTestEmail', 'Send test email')
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dialogTitle: {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
			position: 'relative',
		},
		content: {
			minHeight: 200,
		},
		actions: {
			padding: theme.spacing(1, 3, 3, 3),
		},
		emailTo: {
			fontSize: '16px',
		},
	}),
);

export default SendEmailDialog;
